import { buildActiveLandingPagesPath } from '@/hooks/use-active-landing-pages';
import { CalyxClient, HttpError } from '@platform/http/calyx-client';
import { captureException } from '@/utils/sentry';
import { getInstallationID } from '@/utils/installation-id';
import { partnerSupplier } from '@/utils/environment';
import { useMutation } from '@tanstack/react-query';

export type LandingPageEventType = 'CALL_TO_ACTION' | 'DISMISS' | 'VIEW';

const CONFLICT = 409;
const SERVER_ERROR = 500;
const RETRIES = 2;

export const buildLandingPageEventsPath = (
  businessOrganizationUUID: string,
  supplierUUID: string,
  restaurantUUID: string,
  landingPageUUID: string,
) =>
  `${buildActiveLandingPagesPath(
    businessOrganizationUUID,
    supplierUUID,
    restaurantUUID,
  )}/${encodeURIComponent(landingPageUUID)}/events`;

export interface ReportLandingPageEventVariables {
  landingPageUUID: string;
  type: LandingPageEventType;
}

export interface UseReportLandingPageEventOptions {
  restaurantUUID: string;
  supplierUUID: string;
}

interface UseReportLandingPageEventResult {
  reportEvent: (variables: ReportLandingPageEventVariables) => Promise<void>;
}

/**
 * Reports user interaction with a landing page (viewed, dismissed, press call-to-action)
 *
 * Calyx will pull user info off of the JWT
 */
export const useReportLandingPageEvent = (
  options: UseReportLandingPageEventOptions,
): UseReportLandingPageEventResult => {
  const { restaurantUUID, supplierUUID } = options;
  const businessOrganizationUUID = partnerSupplier.businessOrganizationUUID;

  const { mutateAsync } = useMutation({
    mutationFn: async ({
      landingPageUUID,
      type,
    }: ReportLandingPageEventVariables) => {
      // this resolves to same uuid for all web instances
      const deviceID = await getInstallationID();

      try {
        await CalyxClient.post<unknown>(
          buildLandingPageEventsPath(
            businessOrganizationUUID,
            supplierUUID,
            restaurantUUID,
            landingPageUUID,
          ),
          { device_id: deviceID, type },
        );
      } catch (e) {
        // Calyx will return 409 if type of event for (user, landing page) exists already, treat as no-op
        if (e instanceof HttpError && e.status === CONFLICT) {
          return;
        }
        throw e;
      }
    },
    onError: (e) => captureException(e),
    retry: (failureCount, e) =>
      failureCount < RETRIES &&
      (!(e instanceof HttpError) || e.status >= SERVER_ERROR),
  });

  return { reportEvent: mutateAsync };
};
